import type { ThemeState } from '@atlaskit/tokens';

import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { getEffectiveThemeStateSerialized } from '@confluence/theming-utils/entry-points/getEffectiveTheme.ts';

import { LocalThemeStorage } from './localThemeStorage.ts';
import { RemoteThemeStorage } from './remoteThemeStorage.ts';
import { ErrorSubjectIds, ErrorSubjects, reportThemingError } from './errorHandling.ts';

export class ThemeAPI {
	public static async saveTheme(theme: Partial<ThemeState>): Promise<void> {
		theme.typography = 'typography';

		LocalThemeStorage.saveThemeLocal(theme);
		const { isLoggedIn } = await getSessionData();
		if (isLoggedIn) {
			await RemoteThemeStorage.saveThemeRemote(theme);
		}
	}

	public static async getSavedTheme(): Promise<Partial<ThemeState>> {
		const persistedLocalTheme = ThemeAPI.getSavedThemeLocal();

		const { isLoggedIn } = await getSessionData();

		let persistedRemoteTheme: Partial<ThemeState> | undefined = undefined;
		if (isLoggedIn) {
			try {
				persistedRemoteTheme = await RemoteThemeStorage.getThemeRemote();

				// Theme from the DB should indicate last preference regardless of browser
				if (!ThemeAPI.localAndRemoteAreEqual(persistedLocalTheme, persistedRemoteTheme)) {
					LocalThemeStorage.saveThemeLocal(persistedRemoteTheme);
				}
			} catch (e) {
				// Fall through to local
				const attributes = {
					message: e.message,
					stack: e.stack,
					persistedRemoteTheme,
					persistedLocalTheme,
				};
				await reportThemingError(ErrorSubjects.themeAPI, ErrorSubjectIds.getSavedTheme, attributes);
			}
		}

		const effectiveTheme = persistedRemoteTheme || persistedLocalTheme;
		effectiveTheme.typography = 'typography';
		return effectiveTheme;
	}

	public static getSavedThemeLocal(): Partial<ThemeState> {
		return LocalThemeStorage.getThemeLocal();
	}

	private static localAndRemoteAreEqual(
		local: Partial<ThemeState>,
		remote: Partial<ThemeState>,
	): boolean {
		return getEffectiveThemeStateSerialized(remote) === getEffectiveThemeStateSerialized(local);
	}
}
